import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { default as Table } from 'antd/es/table';
import { useEffect, useState } from 'react';

import { hasValue } from 'utils/hasValue';

import {
  useGetCryptoDeposits,
  type GetCryptoDeposits200DataItem
} from 'api/customerApi';

import { useOrganisationId } from 'hooks/context';

import { DepositDetailModal } from './DepositDetailModal';
import { depositColumns } from './DepositsTableColumn';

import { useVirtualBankAccount } from '../shared/VirtualBankAccountProvider';

export const depositStatusColor: Record<string, string> = {
  SUCCESS: 'bg-status-completed',
  CONFIRMED: 'bg-status-neutral',
  UNCONFIRMED: 'bg-status-neutral',
  SCREENING_REQUESTED: 'bg-status-processing',
  PENDING_WHITELIST: 'bg-status-action-required',
  REJECTED: 'bg-status-negative',
  HELD: 'bg-status-action-required'
};

type PaginationState = {
  page: number;
  cursorAfter?: string;
  cursorBefore?: string;
};

type Props = {
  isActive?: boolean;
};

const INITIAL_PAGINATION: PaginationState = {
  page: 1,
  cursorAfter: undefined,
  cursorBefore: undefined
};

export const DepositsTable = ({ isActive = true }: Props) => {
  const organisationId = useOrganisationId<number>();
  const { virtualAccountNumber } = useVirtualBankAccount();

  const [selectedDeposit, setSelectedDeposit] =
    useState<GetCryptoDeposits200DataItem | null>(null);
  const [pagination, setPagination] =
    useState<PaginationState>(INITIAL_PAGINATION);

  const { data: deposits, isFetching: isDepositsFetching } =
    useGetCryptoDeposits(
      organisationId,
      {
        per_page: 8,
        cursor_after: pagination.cursorAfter,
        cursor_before: pagination.cursorBefore,
        virtual_account_number: virtualAccountNumber
      },
      {
        query: {
          enabled: isActive && hasValue(virtualAccountNumber),
          refetchInterval: pagination.page === 1 && isActive ? 10_000 : false,
          refetchIntervalInBackground: false
        }
      }
    );

  const nextCursor = deposits?.meta?.cursor_next;
  const previousCursor = deposits?.meta?.cursor_prev;
  const totalPages = deposits?.meta?.total
    ? Math.ceil(deposits.meta.total / (deposits.meta.per_page ?? 1))
    : undefined;

  useEffect(() => {
    setPagination(INITIAL_PAGINATION);
  }, [organisationId, virtualAccountNumber]);

  useEffect(() => {
    if (
      (pagination.cursorAfter || pagination.cursorBefore) &&
      !isDepositsFetching &&
      deposits?.data?.length === 0
    ) {
      setPagination(INITIAL_PAGINATION);
    }
  }, [
    deposits?.data?.length,
    isDepositsFetching,
    pagination.cursorAfter,
    pagination.cursorBefore
  ]);

  const handleNext = () => {
    if (nextCursor) {
      setPagination((prev) => ({
        page: prev.page + 1,
        cursorAfter: nextCursor,
        cursorBefore: undefined
      }));
    }
  };

  const handlePrevious = () => {
    if (previousCursor) {
      setPagination((prev) => {
        const newPage = Math.max(prev.page - 1, 1);
        if (newPage === 1) return INITIAL_PAGINATION;
        return {
          page: newPage,
          cursorAfter: undefined,
          cursorBefore: previousCursor
        };
      });
    }
  };

  return (
    <div>
      <Table<GetCryptoDeposits200DataItem>
        dataSource={deposits?.data}
        columns={depositColumns}
        className="h-[500px] overflow-auto"
        rowKey="id"
        size="small"
        pagination={false}
        loading={isDepositsFetching}
        onRow={(record) => ({
          onClick: () => setSelectedDeposit(record),
          style: { cursor: 'pointer' }
        })}
      />
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <LeftOutlined
            disabled={!deposits?.meta?.has_prev_page}
            onClick={handlePrevious}
          />
          <RightOutlined
            disabled={!deposits?.meta?.has_next_page}
            onClick={handleNext}
          />
          <span className="text-sm text-gray-500">
            {totalPages && `${pagination.page} of ${totalPages}`}
          </span>
        </div>
        {deposits?.meta?.total && (
          <span className="my-2 text-sm text-gray-500">
            total: {deposits.meta.total}
          </span>
        )}
      </div>
      {hasValue(selectedDeposit) && (
        <DepositDetailModal
          deposit={selectedDeposit}
          onClose={() => setSelectedDeposit(null)}
        />
      )}
    </div>
  );
};
